import { Badge, Button, Card, Col, Divider, Form, Input, InputNumber, message, Modal, Popconfirm, Radio, Row, Select, Space, Typography } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { useEffect, useState } from 'react';
import { addCommandHubSpToken, addSellerId, addSpToken, addWalmartToken, copyAzinsightSpTokenToCommandHub, deleteSellerId, deleteSpToken, getCatalogItem, getCommandHubConnection, getItemOffers, getMyFeesEstimateForAsin, getWalmartTokenStatus, syncWalmartRecommendations, testCommandHubSpApi, testWalmartApi, testWalmartNotification } from 'services';
import { detectRegion } from 'utils';

const getTypeSpTokenRegion = (source: any, item: string) => source.includes(item) ? 'success' : 'danger'
const getStatusSpTokenRegion = (source: any, item: string) => source.includes(item) ? 'Exist' : 'Non-exist'

// Seller ID shown for AZInsight is only sellerIds on the user for that region.
// A seller ID that exists only on the token document is not displayed.
const sellerIdOnUser = (user: any, region: 'NA' | 'EU') => {
  const value = user?.sellerIDs?.[region]
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed || null
}

const initLoadingField = {
  field: '',
  isLoading: false,
}

const initModalAdd = {
  open: false,
  region: null
}

const initModalWalmart = {
  open: false,
}

const SPApiTab = ({ data }: any) => {
  const [walmartForm] = Form.useForm();
  const [commandHubForm] = Form.useForm();
  const [asin, setAsin] = useState<string>('');
  const [selectedMarketplace, setSelectedMarketplace] = useState<string>('US');
  const [detectedRegion, setDetectedRegion] = useState<string>('NA');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingField, setLoadingField] = useState<any>(initLoadingField);
  const [spApiCall, setSpApiCall] = useState<any>('getCatalogItem');
  const [responseApi, setResponseApi] = useState<any>({});
  const [requestApi, setRequestApi] = useState<any>({});
  const [spTokenRegions, setSpTokenRegions] = useState<any>(data.sp_tokens_regions || [])
  const [sellerIDs, setSellerIds] = useState<any>({
    NA: sellerIdOnUser(data.user, 'NA'),
    EU: sellerIdOnUser(data.user, 'EU'),
  })
  const [spEstPrice, setSpEstPrice] = useState<number>(0);
  const [isValidTokens, setIsValidTokens] = useState<any>({
    NA: null,
    EU: null
  })

  const [modalAddSpToken, setModalAddSpToken] = useState<any>(initModalAdd)
  const [modalAddSellerId, setModalAddSellerId] = useState<any>(initModalAdd)
  const [modalAddWalmartToken, setModalAddWalmartToken] = useState<any>(initModalWalmart)

  const [addSpTokenFields, setAddSpTokenFields] = useState<any>({
    accessToken: undefined,
    refreshToken: undefined
  })
  const [addSellerIdFields, setAddSellerIdFields] = useState<any>({
    sellerId: undefined
  })

  const [walmartStatus, setWalmartStatus] = useState<any>({
    connected: false,
    authorized: false,
    expired: false,
    loading: false
  })

  const [addWalmartTokenFields, setAddWalmartTokenFields] = useState<any>({
    regionShortName: undefined,
    sellerId: undefined,
    accessToken: undefined,
    refreshToken: undefined,
    code: undefined,
  })
  const [walmartMode, setWalmartMode] = useState<'sandbox' | 'manual'>('sandbox');
  const [modalAddCommandHubSpToken, setModalAddCommandHubSpToken] = useState<any>(initModalAdd);
  const [commandHubSeller, setCommandHubSeller] = useState<any>({ NA: null, EU: null });
  const [commandHubConnected, setCommandHubConnected] = useState<any>({ NA: false, EU: false });
  const [commandHubValid, setCommandHubValid] = useState<any>({ NA: null, EU: null });
  const [commandHubWalmart, setCommandHubWalmart] = useState<any>(null);
  const [walmartValid, setWalmartValid] = useState<boolean | null>(null);
  const [testTarget, setTestTarget] = useState<'azinsight' | 'commandhub' | 'walmart'>('azinsight');
  const [walmartTestOp, setWalmartTestOp] = useState<'recommendations' | 'itemSearch'>('recommendations');
  const [walmartQuery, setWalmartQuery] = useState('');

  const userId = data.user._id;

  useEffect(() => {
    loadWalmartStatus();
    loadCommandHubConnection();
  }, [userId]);

  const loadCommandHubConnection = () => {
    getCommandHubConnection(userId)
      .then(({ data: res }: any) => {
        const amazon = res?.data?.amazon;
        const items = Array.isArray(amazon) ? amazon : amazon ? [amazon] : [];
        const next: { NA: string | null; EU: string | null } = { NA: null, EU: null };
        const connected = { NA: false, EU: false };
        items.forEach((item: any) => {
          const region = item?.region;
          if (region !== 'NA' && region !== 'EU') return;
          const sellerId = typeof item.sellerId === 'string' ? item.sellerId.trim() : '';
          next[region as 'NA' | 'EU'] = sellerId || null;
          connected[region as 'NA' | 'EU'] = true;
        });
        setCommandHubSeller(next);
        setCommandHubConnected(connected);
        setCommandHubWalmart(res?.data?.walmart || null);
      })
      .catch(() => {
        setCommandHubSeller({ NA: null, EU: null });
        setCommandHubConnected({ NA: false, EU: false });
        setCommandHubWalmart(null);
      });
  };

  const loadWalmartStatus = () => {
    setWalmartStatus((prev: any) => ({ ...prev, loading: true }));
    getWalmartTokenStatus(userId)
      .then(({ data }: any) => {
        setWalmartStatus({
          connected: data.data?.connected || false,
          authorized: data.data?.authorized || false,
          expired: data.data?.expired || false,
          loading: false,
          sellerId: data.data?.sellerId,
          region: data.data?.region,
        });
      })
      .catch(() => {
        setWalmartStatus((prev: any) => ({ ...prev, loading: false }));
      });
  };

  const handleOnAddWalmartTokenChange = (_: any, allValues: any) => {
    setAddWalmartTokenFields((_: any) => ({
      ...allValues,
    }))
  }

  const handleOnAddWalmartToken = () => {
    const payload = walmartMode === 'sandbox'
      ? {
        code: addWalmartTokenFields.code,
        sellerId: addWalmartTokenFields.sellerId,
        regionShortName: addWalmartTokenFields.regionShortName || 'US',
      }
      : {
        accessToken: addWalmartTokenFields.accessToken,
        refreshToken: addWalmartTokenFields.refreshToken,
        sellerId: addWalmartTokenFields.sellerId,
        regionShortName: addWalmartTokenFields.regionShortName || 'US',
      };
    return addWalmartToken(userId, payload)
      .then(() => {
        message.success('Saved Walmart token successfully');
        loadWalmartStatus();
      })
      .catch((err: any) => {
        message.error(err?.response?.data?.message || "Couldn't save Walmart token");
      })
      .finally(() => {
        setLoadingField(initLoadingField)
        setModalAddWalmartToken(initModalWalmart)
      })
  }

  const hanldeOnValidateSpToken = async (region?: string) => {
    if (region) {
      setLoadingField({ field: `check-valid-${region}`, isLoading: true });
    } else {
      setLoading(true);
    }

    const params = {
      asin: !region ? asin : (region === "NA" ? "B098RSGGGC" : "B000BO6VN0"),
      region: region || detectedRegion,
      marketplace: !region ? selectedMarketplace : (region === "NA" ? "US" : "UK"),
      userId,
      isAmazonFulfilled: true
    }
    let res: any;
    switch (spApiCall) {
      case "getMyFeesEstimateForASIN":
        res = getMyFeesEstimateForAsin({ ...params, price: spEstPrice });
        break;
      case "getItemOffers":
        res = getItemOffers(params);
        break;
      default:
        res = getCatalogItem(params);
    }
    return await res.then(({ data: result }: any) => {
      if (region) {
        setIsValidTokens((prev: any) => ({ ...prev, [region]: true }))
        return message.success('Token is valid');
      }
      setResponseApi(() => {
        setRequestApi(result.config)
        return result.content;
      });
      message.success('Successfully in calling SP API');
    }).catch(({ err }: any) => {
      if (region) {
        setIsValidTokens((prev: any) => ({ ...prev, [region]: false }))
        return message.error('Token is not valid');
      }
      setResponseApi(() => {
        setRequestApi(err.config);
        return err.response.data.detail ?
          err.response.data.detail :
          JSON.parse(err.response.data.substring(1, err.response.data.length - 1))
      });
      message.error('Can not call SP-API');
    })
      .finally(() => {
        if (region) {
          setLoadingField({ field: `check-valid-${region}`, isLoading: false });
        } else {
          setLoading(false);
        }
      })
  };

  const handleOnDeleteSpToken = (id: string, region: string) => {
    setLoadingField({ field: `azinsight-delete-token-${region}`, isLoading: true })
    deleteSpToken(id, region)
      .then(() => {
        message.success('Deleted SP token successfully')
        setSpTokenRegions((prevRegions: any) => prevRegions.filter((r: string) => r !== region))
      })
      .catch(() => {
        message.error('Can not delete SP token')
      })
      .finally(() => setLoadingField(initLoadingField))
  }

  const handleOnDeleteSellerId = (id: string, region: string) => {
    setLoadingField({ field: `azinsight-delete-seller-${region}`, isLoading: true })
    deleteSellerId(id, region)
      .then(() => {
        message.success('Deleted SellerID successfully')
        setSellerIds((prevState: any) => ({
          ...prevState,
          [region]: null,
        }))
      })
      .catch(() => {
        message.error('Can not delete SellerID')
      })
      .finally(() => setLoadingField(initLoadingField))
  }

  const handleOnSelectMarketplace = (selected: string) => {
    setSelectedMarketplace(() => {
      const region = detectRegion(selected)
      setDetectedRegion(region)
      return selected
    });
  }

  const handleOnAddSpTokenChange = (_: any, allValues: any) => {
    setAddSpTokenFields((_: any) => ({
      ...allValues,
    }))
  }

  const handleOnAddSpToken = () => {
    addSpToken(userId, modalAddSpToken.region, addSpTokenFields)
      .then(() => {
        message.success('Added SP Token successfully')
        setSpTokenRegions((prevToken: any) => {
          return [...prevToken, modalAddSpToken.region]
        })
      })
      .catch(() => {
        message.success("Couldn't add SP Token")
      })
      .finally(() =>
        setModalAddSpToken((_: any) => {
          setLoadingField(initLoadingField)
          return initModalAdd
        })
      )
  }

  const handleOnAddSellerIdChange = (_: any, allValues: any) => {
    setAddSellerIdFields((_: any) => ({
      ...allValues,
    }))
  }

  const handleOnAddSellerId = () => {
    addSellerId(userId, modalAddSellerId.region, addSellerIdFields)
      .then(() => {
        message.success('Added Seller Id successfully');
        setSellerIds((prevSellerIds: any) => ({
          ...prevSellerIds,
          [modalAddSellerId.region]: typeof addSellerIdFields.sellerId === 'string'
            ? addSellerIdFields.sellerId.trim() || null
            : null,
        }))
      })
      .catch(() => {
        message.success("Couldn't add Seller Id")
      })
      .finally(() =>
        setModalAddSellerId((_: any) => {
          setLoadingField(initLoadingField)
          return initModalAdd
        })
      )
  }

  const applyTestResult = (payload: any) => {
    setResponseApi(payload?.content ?? payload);
    setRequestApi(payload?.request ?? {});
    message.success('Call succeeded');
  };

  const handleRunSpTest = (source: 'azinsight' | 'commandhub') => {
    if (source === 'azinsight') {
      hanldeOnValidateSpToken();
      return;
    }
    setLoading(true);
    testCommandHubSpApi(userId, {
      operation: spApiCall,
      asin,
      marketplace: selectedMarketplace,
      region: detectedRegion,
      price: spEstPrice,
    })
      .then(({ data: res }: any) => applyTestResult(res?.data || res))
      .catch((err: any) => {
        message.error(err?.response?.data?.message || 'Can not call CommandHub SP-API');
        setResponseApi(err?.response?.data || {});
      })
      .finally(() => setLoading(false));
  };

  const handleCheckCommandHub = (region: string) => {
    if (!commandHubConnected[region]) {
      message.warning(`No CommandHub token for ${region}`);
      return;
    }
    setLoadingField({ field: `check-valid-ch-${region}`, isLoading: true });
    testCommandHubSpApi(userId, {
      operation: 'getCatalogItem',
      asin: region === 'NA' ? 'B098RSGGGC' : 'B000BO6VN0',
      marketplace: region === 'NA' ? 'US' : 'UK',
      region,
    })
      .then(() => {
        setCommandHubValid((prev: any) => ({ ...prev, [region]: true }));
        message.success('CommandHub token is valid');
      })
      .catch((err: any) => {
        setCommandHubValid((prev: any) => ({ ...prev, [region]: false }));
        message.error(err?.response?.data?.message || 'CommandHub token is not valid');
      })
      .finally(() => setLoadingField(initLoadingField));
  };

  const handleSyncWalmart = () => {
    setLoadingField({ field: 'sync-walmart', isLoading: true });
    syncWalmartRecommendations(userId)
      .then(({ data: res }: any) => {
        const result = res?.data?.data || {};
        const upserted = result.upserted ?? 0;
        const matched = result.matched ?? 0;
        message.success(`Synced ${upserted} recommendations (${matched} matched to catalog).`);
      })
      .catch((err: any) => {
        message.error(err?.response?.data?.message || 'Walmart sync failed');
      })
      .finally(() => setLoadingField(initLoadingField));
  };

  const handleTestWalmartNotification = () => {
    setLoadingField({ field: 'test-walmart-notification', isLoading: true });
    testWalmartNotification(userId)
      .then(({ data: res }: any) => {
        const payload = res?.data?.data || res?.data || {};
        const note = payload?.message || payload?.raw || 'Eventurl successfully validated';
        message.success(typeof note === 'string' ? note : 'Walmart accepted the test notification');
        setResponseApi(payload);
      })
      .catch((err: any) => {
        message.error(err?.response?.data?.message || 'Walmart notification test failed');
      })
      .finally(() => setLoadingField(initLoadingField));
  };

  const handleCheckWalmart = () => {
    setLoadingField({ field: 'check-valid-walmart', isLoading: true });
    testWalmartApi(userId, { operation: 'itemSearch', query: 'laptop' })
      .then(() => {
        setWalmartValid(true);
        message.success('Walmart token is valid');
      })
      .catch(() => {
        setWalmartValid(false);
        message.error('Walmart token is not valid');
      })
      .finally(() => setLoadingField(initLoadingField));
  };

  const handleRunWalmartTest = () => {
    setLoading(true);
    testWalmartApi(userId, {
      operation: walmartTestOp,
      query: walmartQuery,
    })
      .then(({ data: res }: any) => applyTestResult(res?.data || res))
      .catch((err: any) => {
        message.error(err?.response?.data?.message || 'Can not call Walmart API');
        setResponseApi(err?.response?.data?.data || err?.response?.data || {});
        setRequestApi(err?.response?.data?.data?.request || {});
      })
      .finally(() => setLoading(false));
  };

  const openSellerModal = (region: string) => {
    setModalAddSellerId({ open: true, region });
  };

  const handleCopyAzinsightTokenToCommandHub = (region: string) => {
    if (getTypeSpTokenRegion(spTokenRegions, region) !== 'success') {
      message.warning('This region has no AZInsight SP token to copy');
      return;
    }
    setLoadingField({ field: `copy-azinsight-token-${region}`, isLoading: true });
    copyAzinsightSpTokenToCommandHub(userId, region)
      .then(({ data }: any) => {
        message.success(data?.message || `Copied AZInsight SP token to CommandHub (${region})`);
        loadCommandHubConnection();
      })
      .catch((err: any) => {
        message.error(err?.response?.data?.message || 'Could not copy AZInsight SP token to CommandHub');
      })
      .finally(() => setLoadingField(initLoadingField));
  };

  return (
    <>
      <Space direction="vertical" style={{ width: "100%", padding: 20 }} size="large">
        <Typography.Title level={4} style={{ marginBottom: 0 }}>Connections</Typography.Title>

        <Card title="SP-API for AZInsight">
          {(['NA', 'EU'] as const).map((region) => (
            <Row gutter={[16, 16]} key={region} align="middle" style={{ marginBottom: 12 }}>
              <Col span={4}>
                <Typography.Text strong>{region === 'NA' ? 'North America' : 'Europe'}</Typography.Text>
              </Col>
              <Col span={3}>
                <Typography.Text type={getTypeSpTokenRegion(spTokenRegions, region)}>
                  {getStatusSpTokenRegion(spTokenRegions, region)}
                </Typography.Text>
              </Col>
              <Col span={3}>
                {getTypeSpTokenRegion(spTokenRegions, region) === 'success' && (
                  <Badge
                    text={isValidTokens[region] === null ? 'Not checked' : (isValidTokens[region] ? 'Valid' : 'Invalid')}
                    status={isValidTokens[region] === null ? 'default' : (isValidTokens[region] ? 'success' : 'error')}
                  />
                )}
              </Col>
              <Col>
                {getTypeSpTokenRegion(spTokenRegions, region) === 'success' && (
                  <Button
                    ghost
                    type="primary"
                    loading={loadingField.field === `check-valid-${region}` && loadingField.isLoading}
                    onClick={() => {
                      setSpApiCall('getCatalogItem');
                      hanldeOnValidateSpToken(region);
                    }}
                  >
                    Check valid
                  </Button>
                )}
              </Col>
              <Col>
                {getTypeSpTokenRegion(spTokenRegions, region) === 'success' ? (
                  <Popconfirm title="Delete this SP token?" onConfirm={() => handleOnDeleteSpToken(userId, region)}>
                    <Button danger loading={loadingField.field === `azinsight-delete-token-${region}` && loadingField.isLoading}>Delete token</Button>
                  </Popconfirm>
                ) : (
                  <Button onClick={() => setModalAddSpToken({ open: true, region })}>Add token</Button>
                )}
              </Col>
              <Col span={5}>
                <Typography.Text>
                  Seller ID: {sellerIDs[region] || '—'}
                </Typography.Text>
              </Col>
              <Col>
                {sellerIDs[region] ? (
                  <Popconfirm title="Delete this seller ID?" onConfirm={() => handleOnDeleteSellerId(userId, region)}>
                    <Button danger loading={loadingField.field === `azinsight-delete-seller-${region}` && loadingField.isLoading}>Delete seller ID</Button>
                  </Popconfirm>
                ) : (
                  <Button onClick={() => openSellerModal(region)}>Add seller ID</Button>
                )}
              </Col>
            </Row>
          ))}
        </Card>

        <Card title="SP-API for CommandHub">
          {(['NA', 'EU'] as const).map((region) => (
            <Row gutter={[16, 16]} key={`ch-${region}`} align="middle" style={{ marginBottom: 12 }}>
              <Col span={4}>
                <Typography.Text strong>{region === 'NA' ? 'North America' : 'Europe'}</Typography.Text>
              </Col>
              <Col span={3}>
                <Badge
                  text={commandHubValid[region] === null ? 'Not checked' : (commandHubValid[region] ? 'Valid' : 'Invalid')}
                  status={commandHubValid[region] === null ? 'default' : (commandHubValid[region] ? 'success' : 'error')}
                />
              </Col>
              <Col>
                <Button
                  ghost
                  type="primary"
                  disabled={!commandHubConnected[region]}
                  loading={loadingField.field === `check-valid-ch-${region}` && loadingField.isLoading}
                  onClick={() => handleCheckCommandHub(region)}
                >
                  Check valid
                </Button>
              </Col>
              <Col>
                <Button onClick={() => {
                  commandHubForm.resetFields();
                  setModalAddCommandHubSpToken({ open: true, region });
                }}>
                  Add token
                </Button>
              </Col>
              <Col>
                <Button
                  disabled={getTypeSpTokenRegion(spTokenRegions, region) !== 'success'}
                  loading={loadingField.field === `copy-azinsight-token-${region}` && loadingField.isLoading}
                  onClick={() => handleCopyAzinsightTokenToCommandHub(region)}
                >
                  Copy AZInsight token
                </Button>
              </Col>
              <Col span={5}>
                <Typography.Text>Seller ID: {commandHubSeller[region] || '—'}</Typography.Text>
              </Col>
            </Row>
          ))}
        </Card>

        <Card title="Walmart API">
          <Row gutter={[16, 16]} align="middle">
            <Col span={4}>
              <Typography.Text strong>Region</Typography.Text>
            </Col>
            <Col span={4}>{walmartStatus.region || '—'}</Col>
            <Col span={3}>
              <Typography.Text strong>Seller ID</Typography.Text>
            </Col>
            <Col span={4}>{walmartStatus.sellerId || '—'}</Col>
            <Col>
              {walmartStatus.loading ? <Typography.Text>Loading...</Typography.Text> : (
                <Badge
                  text={!walmartStatus.connected ? 'Not connected' : walmartStatus.expired ? 'Expired' : 'Connected'}
                  status={!walmartStatus.connected ? 'default' : walmartStatus.expired ? 'warning' : 'success'}
                />
              )}
            </Col>
            <Col>
              <Badge
                text={walmartValid === null ? 'Not checked' : walmartValid ? 'Valid' : 'Invalid'}
                status={walmartValid === null ? 'default' : walmartValid ? 'success' : 'error'}
              />
            </Col>
            <Col>
              <Badge
                text={
                  commandHubWalmart?.assortmentWebhookSubscribed
                    ? (commandHubWalmart?.assortmentWebhookSubscribedAt
                      ? `Assortment recommendations: Subscribed (${commandHubWalmart.assortmentWebhookSubscribedAt})`
                      : 'Assortment recommendations: Subscribed')
                    : 'Assortment recommendations: Not subscribed'
                }
                status={commandHubWalmart?.assortmentWebhookSubscribed ? 'success' : 'default'}
              />
            </Col>
            <Col>
              <Button
                ghost
                type="primary"
                disabled={!walmartStatus.connected}
                loading={loadingField.field === 'check-valid-walmart' && loadingField.isLoading}
                onClick={handleCheckWalmart}
              >
                Check valid
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                disabled={!walmartStatus.connected}
                loading={loadingField.field === 'sync-walmart' && loadingField.isLoading}
                onClick={handleSyncWalmart}
              >
                Sync from Walmart
              </Button>
            </Col>
            <Col>
              <Button
                disabled={!walmartStatus.connected}
                loading={loadingField.field === 'test-walmart-notification' && loadingField.isLoading}
                onClick={handleTestWalmartNotification}
              >
                Test notification
              </Button>
            </Col>
            <Col>
              <Button type="primary" onClick={() => {
                const next = {
                  regionShortName: walmartStatus.region || 'US',
                  sellerId: walmartStatus.sellerId,
                  accessToken: undefined,
                  refreshToken: undefined,
                  code: undefined,
                };
                setWalmartMode(walmartStatus.connected ? 'manual' : 'sandbox');
                setAddWalmartTokenFields(next);
                walmartForm.setFieldsValue(next);
                setModalAddWalmartToken({ open: true });
              }}>
                {walmartStatus.connected ? 'Update token' : 'Add token'}
              </Button>
            </Col>
          </Row>
        </Card>

        <Typography.Title level={4} style={{ marginBottom: 0 }}>Testing</Typography.Title>
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          value={testTarget}
          onChange={(e) => {
            setTestTarget(e.target.value);
            setResponseApi({});
            setRequestApi({});
          }}
        >
          <Radio.Button value="azinsight">SP-API for AZInsight</Radio.Button>
          <Radio.Button value="commandhub">SP-API for CommandHub</Radio.Button>
          <Radio.Button value="walmart">Walmart API</Radio.Button>
        </Radio.Group>

        {testTarget !== 'walmart' ? (
          <Card title={testTarget === 'azinsight' ? 'Call SP-API with the AZInsight token' : 'Call SP-API with the CommandHub token'}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Row gutter={[16, 16]} align="middle">
                <Col span={3}><Typography.Text strong>ASIN</Typography.Text></Col>
                <Col span={10}><Input value={asin} onChange={(e) => setAsin(e.target.value)} disabled={loading} /></Col>
              </Row>
              <Row gutter={[16, 16]} align="middle">
                <Col span={3}><Typography.Text strong>API</Typography.Text></Col>
                <Col span={8}>
                  <Select
                    disabled={loading}
                    value={spApiCall}
                    style={{ width: '100%' }}
                    options={[
                      { label: 'GetCatalogItem()', value: 'getCatalogItem' },
                      { label: 'GetMyFeesEstimateForASIN()', value: 'getMyFeesEstimateForASIN' },
                      { label: 'GetItemOffers()', value: 'getItemOffers' },
                    ]}
                    onSelect={(value: string) => setSpApiCall(value)}
                  />
                </Col>
                {spApiCall === 'getMyFeesEstimateForASIN' && (
                  <Col>
                    <InputNumber
                      value={spEstPrice}
                      onChange={(val) => setSpEstPrice(val ?? 0)}
                      addonBefore="Price"
                    />
                  </Col>
                )}
              </Row>
              <Row gutter={[16, 16]} align="middle">
                <Col span={3}><Typography.Text strong>Marketplace</Typography.Text></Col>
                <Col span={6}>
                  <Select
                    disabled={loading}
                    value={selectedMarketplace}
                    style={{ width: '100%' }}
                    onSelect={handleOnSelectMarketplace}
                    options={['US', 'UK', 'CA', 'MX', 'DE', 'FR', 'IT', 'ES'].map((value) => ({ label: value, value }))}
                  />
                </Col>
                <Col>
                  <Button type="primary" loading={loading} onClick={() => handleRunSpTest(testTarget === 'commandhub' ? 'commandhub' : 'azinsight')}>Run</Button>
                </Col>
              </Row>
            </Space>
          </Card>
        ) : (
          <Card title="Call Walmart API with the portal token">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Row gutter={[16, 16]} align="middle">
                <Col span={3}><Typography.Text strong>API</Typography.Text></Col>
                <Col span={10}>
                  <Select
                    value={walmartTestOp}
                    style={{ width: '100%' }}
                    onChange={(value) => setWalmartTestOp(value)}
                    options={[
                      { label: 'Assortment recommendations', value: 'recommendations' },
                      { label: 'Item search', value: 'itemSearch' },
                    ]}
                  />
                </Col>
              </Row>
              {walmartTestOp === 'itemSearch' && (
                <Row gutter={[16, 16]} align="middle">
                  <Col span={3}><Typography.Text strong>Query</Typography.Text></Col>
                  <Col span={10}>
                    <Input value={walmartQuery} placeholder="Search text" onChange={(e) => setWalmartQuery(e.target.value)} />
                  </Col>
                </Row>
              )}
              <Button type="primary" loading={loading} onClick={handleRunWalmartTest}>Run</Button>
            </Space>
          </Card>
        )}

        {Object.keys(responseApi).length > 0 && (
          <Card title="Result">
            <Typography.Text strong>Request</Typography.Text>
            <TextArea rows={8} value={JSON.stringify(requestApi, null, 2)} />
            <Typography.Text strong>Response</Typography.Text>
            <TextArea rows={16} value={JSON.stringify(responseApi, null, 2)} />
          </Card>
        )}
      </Space>
      <Modal
        title="Add SP Token"
        visible={modalAddSpToken.open}
        confirmLoading={loadingField.field === `azinsight-add-token-${modalAddSpToken.region}` && loadingField.isLoading}
        onOk={() => {
          setLoadingField({ field: `azinsight-add-token-${modalAddSpToken.region}`, isLoading: true })
          handleOnAddSpToken()
        }}
        onCancel={() => setModalAddSpToken(initModalAdd)}
        okText="Add"
        destroyOnClose
      >
        <Form
          name="basic"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          autoComplete="off"
          onValuesChange={handleOnAddSpTokenChange}
        >
          <Form.Item
            label="Access Token"
            name="accessToken"
            labelAlign="left"
            rules={[{ required: true, message: 'Please input your access token!' }]}
          >
            <Input allowClear value={addSpTokenFields.accessToken} />
          </Form.Item>
          <Form.Item
            label="Refresh Token"
            name="refreshToken"
            labelAlign="left"
            rules={[{ required: true, message: 'Please input your refresh token!' }]}
          >
            <Input allowClear value={addSpTokenFields.refreshToken} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Add Seller ID"
        visible={modalAddSellerId.open}
        confirmLoading={loadingField.field === `azinsight-add-seller-${modalAddSellerId.region}` && loadingField.isLoading}
        onOk={() => {
          setLoadingField({
            field: `azinsight-add-seller-${modalAddSellerId.region}`,
            isLoading: true,
          })
          handleOnAddSellerId()
        }}
        onCancel={() => setModalAddSellerId(initModalAdd)}
        okText="Add"
        destroyOnClose
      >
        <Form
          name="basic"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          autoComplete="off"
          onValuesChange={handleOnAddSellerIdChange}
        >
          <Form.Item
            label="Seller ID"
            name="sellerId"
            labelAlign="left"
            rules={[{ required: true, message: 'Please input Seller ID!' }]}
          >
            <Input allowClear value={addSellerIdFields.sellerId} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={walmartStatus.connected ? "Update Walmart Token" : "Add Walmart Token"}
        visible={modalAddWalmartToken.open}
        confirmLoading={loadingField.field === 'walmart-add-token' && loadingField.isLoading}
        onOk={() => {
          walmartForm.validateFields().then(() => {
            setLoadingField({ field: 'walmart-add-token', isLoading: true })
            handleOnAddWalmartToken()
          }).catch(() => undefined)
        }}
        onCancel={() => {
          setModalAddWalmartToken(initModalWalmart)
          walmartForm.resetFields()
        }}
        okText={walmartStatus.connected ? "Update" : "Add"}
        destroyOnClose
      >
        <Form
          name="walmart-token-form"
          form={walmartForm}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          autoComplete="off"
          onValuesChange={handleOnAddWalmartTokenChange}
        >
          <Form.Item
            label="Region"
            name="regionShortName"
            labelAlign="left"
            rules={[{ required: true, message: 'Please input region (e.g. US)!' }]}
          >
            <Input allowClear />
          </Form.Item>
          <Form.Item
            label="Seller ID"
            name="sellerId"
            labelAlign="left"
            rules={[{ required: true, message: 'Please input seller id!' }]}
          >
            <Input allowClear />
          </Form.Item>
          <Form.Item label="Method" labelAlign="left">
            <Radio.Group value={walmartMode} onChange={(e) => setWalmartMode(e.target.value)}>
              <Radio value="sandbox">Sandbox authorization code</Radio>
              <Radio value="manual">Manual tokens</Radio>
            </Radio.Group>
          </Form.Item>
          {walmartMode === 'sandbox' ? (
            <Form.Item
              label="Authorization code"
              name="code"
              labelAlign="left"
              rules={[{ required: true, message: 'Please paste the sandbox authorization code!' }]}
            >
              <Input.TextArea rows={3} allowClear placeholder="Paste the sandbox Demo Connect code" />
            </Form.Item>
          ) : (
            <>
              <Form.Item
                label="Access Token"
                name="accessToken"
                labelAlign="left"
                rules={[{ required: true, message: 'Please input access token!' }]}
              >
                <Input allowClear />
              </Form.Item>
              <Form.Item
                label="Refresh Token"
                name="refreshToken"
                labelAlign="left"
                rules={[{ required: true, message: 'Please input refresh token!' }]}
              >
                <Input allowClear />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
      <Modal
        title={`Add CommandHub SP Token (${modalAddCommandHubSpToken.region || ''})`}
        visible={modalAddCommandHubSpToken.open}
        confirmLoading={loadingField.field === `commandhub-add-token-${modalAddCommandHubSpToken.region}` && loadingField.isLoading}
        onOk={() => {
          commandHubForm.validateFields().then((values) => {
            setLoadingField({ field: `commandhub-add-token-${modalAddCommandHubSpToken.region}`, isLoading: true })
            addCommandHubSpToken(userId, {
              ...values,
              region: modalAddCommandHubSpToken.region,
            })
              .then(() => {
                message.success('Added CommandHub SP token successfully');
                loadCommandHubConnection();
              })
              .catch((err: any) => {
                message.error(err?.response?.data?.message || "Couldn't add CommandHub SP token");
              })
              .finally(() => {
                setLoadingField(initLoadingField);
                setModalAddCommandHubSpToken(initModalAdd);
                commandHubForm.resetFields();
              })
          }).catch(() => undefined)
        }}
        onCancel={() => setModalAddCommandHubSpToken(initModalAdd)}
        okText="Add"
        destroyOnClose
      >
        <Form
          name="commandhub-sp-token"
          form={commandHubForm}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          autoComplete="off"
        >
          <Form.Item
            label="Seller ID"
            name="sellerId"
            labelAlign="left"
            rules={[{ required: true, message: 'Please input seller ID!' }]}
          >
            <Input allowClear />
          </Form.Item>
          <Form.Item
            label="Access Token"
            name="accessToken"
            labelAlign="left"
            rules={[{ required: true, message: 'Please input access token!' }]}
          >
            <Input allowClear />
          </Form.Item>
          <Form.Item
            label="Refresh Token"
            name="refreshToken"
            labelAlign="left"
            rules={[{ required: true, message: 'Please input refresh token!' }]}
          >
            <Input allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SPApiTab;